import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
} from "react";
import { ContextProviderProps } from "../../../types/ContextProviderProps";
import { useOrdersService } from "../../../hooks/use-portfolio-service/useOrdersService";
import {
    AutoOrderConfigurationState,
    OrdersState,
    PortfolioManagementState,
    PortfolioProfileState,
    PortfolioSelectionState,
} from "../types/PortfolioContextState";

const PortfolioSelectionContext = createContext<PortfolioSelectionState | undefined>(undefined);
const PortfolioManagementContext = createContext<PortfolioManagementState | undefined>(undefined);
const PortfolioProfileContext = createContext<PortfolioProfileState | undefined>(undefined);
const OrdersContext = createContext<OrdersState | undefined>(undefined);
const AutoOrderConfigurationContext = createContext<AutoOrderConfigurationState | undefined>(undefined);

type Callback = (...args: any[]) => any;

function useStableCallback<T extends Callback>(callback: T): T {
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    const stableCallback = useCallback((...args: Parameters<T>): ReturnType<T> =>
        callbackRef.current(...args), []);

    return stableCallback as T;
}

export function PortfolioContextProvider({ children }: ContextProviderProps) {
    const service = useOrdersService();

    const setSelectedPortfolioGroupByName = useStableCallback(service.setSelectedPortfolioGroupByName);
    const setSelectedPortfolioByName = useStableCallback(service.setSelectedPortfolioByName);
    const setAllPortfoliosByUnderlyingAsset = useStableCallback(service.setAllPortfoliosByUnderlyingAsset);
    const createPortfolioGroup = useStableCallback(service.createPortfolioGroup);
    const editSelectedPortfolioGroup = useStableCallback(service.editSelectedPortfolioGroup);
    const deleteSelectedPortfolioGroup = useStableCallback(service.deleteSelectedPortfolioGroup);
    const createPortfolio = useStableCallback(service.createPortfolio);
    const editSelectedPortfolio = useStableCallback(service.editSelectedPortfolio);
    const saveSelectedPortfolio = useStableCallback(service.saveSelectedPortfolio);
    const fetchSelectedPortfolio = useStableCallback(service.fetchSelectedPortfolio);
    const deleteSelectedPortfolio = useStableCallback(service.deleteSelectedPortfolio);
    const cloneSelectedPortfolio = useStableCallback(service.cloneSelectedPortfolio);
    const setSelectedComparedPortfolioByName = useStableCallback(service.setSelectedComparedPortfolioByName);
    const refreshPortfolioProfile = useStableCallback(service.refreshPortfolioProfile);
    const updateSelectedPortfolio = useStableCallback(service.updateSelectedPortfolio);
    const addOptionToPortfolio = useStableCallback(service.addOptionToPortfolio);
    const addFutureToPortfolio = useStableCallback(service.addFutureToPortfolio);
    const addFuturesToPortfolio = useStableCallback(service.addFuturesToPortfolio);
    const updateOrderInSelectedPortfolio = useStableCallback(service.updateOrderInSelectedPortfolio);
    const deleteOrderFromSelectedPortfolio = useStableCallback(service.deleteOrderFromSelectedPortfolio);
    const updatePositionInSelectedPortfolio = useStableCallback(service.updatePositionInSelectedPortfolio);
    const updatePositionsInSelectedPortfolio = useStableCallback(service.updatePositionsInSelectedPortfolio);
    const saveCustomImpliedVolatility = useStableCallback(service.saveCustomImpliedVolatility);
    const saveAutoOrderConfiguration = useStableCallback(service.saveAutoOrderConfiguration);
    const fetchAutoOrderConfiguration = useStableCallback(service.fetchAutoOrderConfiguration);
    const resetAutoOrderConfigurationState = useStableCallback(service.resetAutoOrderConfigurationState);

    const selectionValue = useMemo<PortfolioSelectionState>(() => ({
        portfolioGroups: service.portfolioGroups,
        selectedPortfolioGroup: service.selectedPortfolioGroup,
        portfolios: service.portfolios,
        underlyingAssetPortfolios: service.underlyingAssetPortfolios,
        selectedPortfolio: service.selectedPortfolio,
        isAllPortfoliosSelected: service.isAllPortfoliosSelected,
        selectedPortfolioDates: service.selectedPortfolioDates,
        setSelectedPortfolioGroupByName,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
    }), [
        service.portfolioGroups,
        service.selectedPortfolioGroup,
        service.portfolios,
        service.underlyingAssetPortfolios,
        service.selectedPortfolio,
        service.isAllPortfoliosSelected,
        service.selectedPortfolioDates,
        setSelectedPortfolioGroupByName,
        setSelectedPortfolioByName,
        setAllPortfoliosByUnderlyingAsset,
    ]);

    const managementValue = useMemo<PortfolioManagementState>(() => ({
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        createPortfolio,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        cloneSelectedPortfolio,
    }), [
        createPortfolioGroup,
        editSelectedPortfolioGroup,
        deleteSelectedPortfolioGroup,
        createPortfolio,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        cloneSelectedPortfolio,
    ]);

    const profileValue = useMemo<PortfolioProfileState>(() => ({
        portfolioProfile: service.portfolioProfile,
        comparedPortfolioProfile: service.comparedPortfolioProfile,
        selectedComparedPortfolio: service.selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshPortfolioProfile,
        updateSelectedPortfolio,
    }), [
        service.portfolioProfile,
        service.comparedPortfolioProfile,
        service.selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshPortfolioProfile,
        updateSelectedPortfolio,
    ]);

    const itemsValue = useMemo<OrdersState>(() => ({
        marketDataDto: service.marketDataDto,
        addOptionToPortfolio,
        addFutureToPortfolio,
        addFuturesToPortfolio,
        updateOrderInSelectedPortfolio,
        deleteOrderFromSelectedPortfolio,
        updatePositionInSelectedPortfolio,
        updatePositionsInSelectedPortfolio,
        saveCustomImpliedVolatility,
    }), [
        service.marketDataDto,
        addOptionToPortfolio,
        addFutureToPortfolio,
        addFuturesToPortfolio,
        updateOrderInSelectedPortfolio,
        deleteOrderFromSelectedPortfolio,
        updatePositionInSelectedPortfolio,
        updatePositionsInSelectedPortfolio,
        saveCustomImpliedVolatility,
    ]);

    const autoOrderValue = useMemo<AutoOrderConfigurationState>(() => ({
        saveAutoOrderConfiguration,
        fetchAutoOrderConfiguration,
        resetAutoOrderConfigurationState,
    }), [
        saveAutoOrderConfiguration,
        fetchAutoOrderConfiguration,
        resetAutoOrderConfigurationState,
    ]);

    return (
        <PortfolioSelectionContext.Provider value={selectionValue}>
            <PortfolioManagementContext.Provider value={managementValue}>
                <PortfolioProfileContext.Provider value={profileValue}>
                    <OrdersContext.Provider value={itemsValue}>
                        <AutoOrderConfigurationContext.Provider value={autoOrderValue}>
                            {children}
                        </AutoOrderConfigurationContext.Provider>
                    </OrdersContext.Provider>
                </PortfolioProfileContext.Provider>
            </PortfolioManagementContext.Provider>
        </PortfolioSelectionContext.Provider>
    );
}

function useRequiredContext<T>(context: T | undefined, hookName: string): T {
    if (context === undefined) {
        throw new Error(`${hookName} must be used within PortfolioContextProvider.`);
    }

    return context;
}

export const usePortfolioSelection = () =>
    useRequiredContext(useContext(PortfolioSelectionContext), "usePortfolioSelection");

export const usePortfolioManagement = () =>
    useRequiredContext(useContext(PortfolioManagementContext), "usePortfolioManagement");

export const usePortfolioProfile = () =>
    useRequiredContext(useContext(PortfolioProfileContext), "usePortfolioProfile");

export const useOrders = () =>
    useRequiredContext(useContext(OrdersContext), "useOrders");

export const useAutoOrderConfiguration = () =>
    useRequiredContext(useContext(AutoOrderConfigurationContext), "useAutoOrderConfiguration");
